import { ShiftManagementDeleteCoverageDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useFilters } from 'modules/shift_management/hooks/useFilters'
import type { View } from 'modules/shift_management/lib/types'

import { Footer } from './Footer'

interface Props {
  onClose: () => void
  view: View
}

const DeleteCoverageModal = ({ onClose, view }: Props) => {
  const { access } = useCurrentSession()
  const locale = access.locale || 'en'
  const startDate = new Date(view.startAt)
  const endDate = new Date(view.endAt)

  const [{ locationIds }] = useFilters()
  const currentLocation = locationIds.at(0)

  const formattedStartDate = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    weekday: 'short',
    month: 'short',
  }).format(startDate)

  const formattedEndDate = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    weekday: 'short',
    month: 'short',
    year: 'numeric',
  }).format(endDate)

  const dateRange = ` ${formattedStartDate} - ${formattedEndDate} `
  const description = i18n.t(
    'shift_management.coverage_section.delete_coverage_modal.description',
    {
      date: dateRange,
    }
  )

  const [deleteCoverage] = useMutation(ShiftManagementDeleteCoverageDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementUnassignedShift,
  })

  const onSubmit = async (closePortal: ClosePortalFn) => {
    track(trackingEvents.shiftManagement.deleteCoverage, {
      from: 'coverage_section',
    })

    const res = await deleteCoverage({
      variables: {
        endAt: view.endAt,
        locationIds: currentLocation ? [String(currentLocation)] : [],
        startAt: view.startAt,
      },
      update: (cache, { data }) => {
        const deletedShifts =
          data?.shiftManagementMutations.bulkDeleteShiftmanagementUnassignedShift.unassignedShifts
        if (!deletedShifts) return

        deletedShifts.forEach((shift) => {
          cache.evict({
            id: cache.identify(shift),
          })
        })
      },
    })

    const handled = handleResult(res, (_errors) => {})

    if (handled.success) {
      track(trackingEvents.shiftManagement.deleteCoverageSubmit)
      closePortal()
    }
  }

  return (
    <Modal
      title={i18n.t('shift_management.coverage_section.delete_coverage_modal.title')}
      description={description}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={<Footer onClose={onClose} />}
    ></Modal>
  )
}

export { DeleteCoverageModal }
